import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import styled from 'styled-components';

const InstallationContainer = styled.section`
  min-height: 100vh;
  padding: 5rem 2rem;
  background: linear-gradient(135deg, #065f46 0%, #047857 50%, #059669 100%);
`;

const Content = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const SectionTitle = styled(motion.h2)`
  font-size: clamp(2.5rem, 5vw, 4rem);
  text-align: center;
  margin-bottom: 3rem;
  background: linear-gradient(135deg, #3b82f6, #06b6d4);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  font-weight: 800;
`;

const StepsContainer = styled.div`
  display: grid;
  gap: 2rem;
`;

const StepCard = styled(motion.div)`
  background: rgba(255, 255, 255, 0.1);
  border-radius: 20px;
  padding: 2rem;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  cursor: pointer;
`;

const StepHeader = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 1rem;
`;

const StepNumber = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(135deg, #3b82f6, #1d4ed8);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 700;
  margin-right: 1rem;
`;

const StepTitle = styled.h3`
  color: #ffffff;
  font-size: 1.3rem;
  font-weight: 600;
`;

const StepDescription = styled.p`
  color: #94a3b8;
  line-height: 1.6;
  margin-bottom: 1rem;
`;

const StepDetails = styled(motion.div)`
  background: rgba(255, 255, 255, 0.05);
  border-radius: 12px;
  padding: 1.5rem;
  margin-top: 1rem;
`;

const DetailsList = styled.ul`
  list-style: none;
  padding: 0;
`;

const DetailItem = styled.li`
  color: #e2e8f0;
  padding: 0.5rem 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  
  &:last-child {
    border-bottom: none;
  }
  
  &:before {
    content: '✓';
    color: #10b981;
    font-weight: bold;
    margin-right: 0.5rem;
  }
`;

const SafetyNote = styled.div`
  background: rgba(239, 68, 68, 0.2);
  border: 1px solid rgba(239, 68, 68, 0.3);
  border-radius: 12px;
  padding: 1.5rem;
  margin-top: 2rem;
`;

const SafetyTitle = styled.h4`
  color: #ef4444;
  margin-bottom: 1rem;
  font-weight: 600;
`;

function InstallationGuide() {
  const [expandedStep, setExpandedStep] = useState(null);

  const installationSteps = [
    {
      id: 1,
      title: 'Initial Positioning',
      description: 'Position first rebar and hand-thread into coupler',
      duration: '30 seconds',
      details: [
        'Ensure straight alignment of rebar with coupler',
        'Thread engagement minimum 6 full threads',
        'Check for cross-threading before proceeding',
        'Verify correct coupler size and type'
      ]
    },
    {
      id: 2,
      title: 'First Bar Tightening',
      description: 'Tighten first bar to specified torque',
      duration: '45 seconds',
      details: [
        'Use calibrated torque wrench only',
        'Apply torque gradually in stages',
        'M20: 150-180 N⋅m, M25: 280-320 N⋅m',
        'M32: 500-600 N⋅m, M40: 800-1000 N⋅m'
      ]
    },
    {
      id: 3,
      title: 'Second Bar Installation',
      description: 'Thread second rebar into opposite end of coupler',
      duration: '30 seconds',
      details: [
        'Maintain proper alignment during threading',
        'Avoid cross-threading at all costs',
        'Visual verification of full thread engagement',
        'Check for smooth threading operation'
      ]
    },
    {
      id: 4,
      title: 'Final Tightening',
      description: 'Apply final torque to complete connection',
      duration: '45 seconds',
      details: [
        'Follow same torque specification as first bar',
        'Mark connection for quality inspection',
        'Record torque values and installer ID',
        'Document installation time and conditions'
      ]
    },
    {
      id: 5,
      title: 'Quality Verification',
      description: 'Inspect completed connection',
      duration: '30 seconds',
      details: [
        'Verify proper thread engagement on both ends',
        'Confirm correct torque was applied',
        'Check for visible damage or defects',
        'Obtain sign-off from qualified inspector'
      ]
    }
  ];

  const safetyGuidelines = [
    'Always wear safety glasses and protective gloves',
    'Ensure stable footing and proper body position',
    'Keep threading oil away from ignition sources',
    'Use proper lifting techniques for heavy bars',
    'Maintain clean and organized work area',
    'Never exceed specified torque values'
  ];

  return (
    <InstallationContainer id="installation-guide" data-section="installation-guide">
      <Content>
        <SectionTitle
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          Installation Guide
        </SectionTitle>

        <StepsContainer>
          {installationSteps.map((step) => (
            <StepCard
              key={step.id}
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ delay: step.id * 0.1 }}
              whileHover={{ scale: 1.02 }}
              onClick={() => setExpandedStep(expandedStep === step.id ? null : step.id)}
            >
              <StepHeader>
                <StepNumber>{step.id}</StepNumber>
                <div>
                  <StepTitle>{step.title}</StepTitle>
                  <div style={{ color: '#10b981', fontSize: '0.9rem', fontWeight: '500' }}>
                    Duration: {step.duration}
                  </div>
                </div>
              </StepHeader>
              
              <StepDescription>{step.description}</StepDescription>

              <AnimatePresence>
                {expandedStep === step.id && (
                  <StepDetails
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <h4 style={{ color: '#3b82f6', marginBottom: '1rem' }}>Key Points:</h4>
                    <DetailsList>
                      {step.details.map((detail, index) => (
                        <DetailItem key={index}>{detail}</DetailItem>
                      ))}
                    </DetailsList>
                  </StepDetails>
                )}
              </AnimatePresence>
            </StepCard>
          ))}
        </StepsContainer>

        <SafetyNote>
          <SafetyTitle>⚠️ Safety Guidelines</SafetyTitle>
          <DetailsList>
            {safetyGuidelines.map((guideline, index) => (
              <DetailItem key={index} style={{ color: '#fecaca' }}>
                {guideline}
              </DetailItem>
            ))}
          </DetailsList>
        </SafetyNote>
      </Content>
    </InstallationContainer>
  );
}

export default InstallationGuide;